import React, { useState } from 'react';
import { MainLayout } from '@/components/Layout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { AlertTriangle, Trash2, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';

export function HeatmapPage() {
  return (
    <MainLayout>
      <div className="space-y-8 page-enter" data-testid="heatmap-page">
        <div>
          <h1 className="text-4xl font-bold text-slate-900 mb-2" style={{ fontFamily: 'Barlow Condensed' }}>
            Mapa de Calor
          </h1>
          <p className="text-slate-600">Visualize suas entregas no mapa</p>
        </div>
        <div className="bg-white rounded-xl shadow-sm border border-slate-100 p-12 text-center">
          <div className="max-w-md mx-auto">
            <div className="text-6xl mb-4">🗺️</div>
            <h3 className="text-2xl font-bold text-slate-900 mb-2">Mapa de Calor</h3>
            <p className="text-slate-600">
              Visualização de entregas por região com integração Google Maps.
              <br />
              <span className="text-sm text-slate-500 mt-2 block">(Módulo em desenvolvimento)</span>
            </p>
          </div>
        </div>
      </div>
    </MainLayout>
  );
}

export function CashPage() {
  return (
    <MainLayout>
      <div className="space-y-8 page-enter" data-testid="cash-page">
        <div>
          <h1 className="text-4xl font-bold text-slate-900 mb-2" style={{ fontFamily: 'Barlow Condensed' }}>
            Caixa
          </h1>
          <p className="text-slate-600">Controle de caixa e movimentações</p>
        </div>
        <div className="bg-white rounded-xl shadow-sm border border-slate-100 p-12 text-center">
          <div className="max-w-md mx-auto">
            <div className="text-6xl mb-4">💵</div>
            <h3 className="text-2xl font-bold text-slate-900 mb-2">Módulo de Caixa</h3>
            <p className="text-slate-600">
              Abertura/fechamento de caixa, sangria, suprimento e conferência diária.
              <br />
              <span className="text-sm text-slate-500 mt-2 block">(Módulo em desenvolvimento)</span>
            </p>
          </div>
        </div>
      </div>
    </MainLayout>
  );
}

export function ReportsPage() {
  return (
    <MainLayout>
      <div className="space-y-8 page-enter" data-testid="reports-page">
        <div>
          <h1 className="text-4xl font-bold text-slate-900 mb-2" style={{ fontFamily: 'Barlow Condensed' }}>
            Relatórios
          </h1>
          <p className="text-slate-600">Relatórios gerenciais completos</p>
        </div>
        <div className="bg-white rounded-xl shadow-sm border border-slate-100 p-12 text-center">
          <div className="max-w-md mx-auto">
            <div className="text-6xl mb-4">📊</div>
            <h3 className="text-2xl font-bold text-slate-900 mb-2">Módulo de Relatórios</h3>
            <p className="text-slate-600">
              Relatórios de vendas, estoque, financeiro, fiscal com exportação PDF/Excel.
              <br />
              <span className="text-sm text-slate-500 mt-2 block">(Módulo em desenvolvimento)</span>
            </p>
          </div>
        </div>
      </div>
    </MainLayout>
  );
}

export function SettingsPage() {
  const [showCleanupModal, setShowCleanupModal] = useState(false);
  const [confirmText, setConfirmText] = useState('');
  const [cleaning, setCleaning] = useState(false);

  const handleCleanup = async () => {
    if (confirmText !== 'CONFIRMAR') {
      toast.error('Digite CONFIRMAR para prosseguir');
      return;
    }

    setCleaning(true);
    try {
      const response = await api.post('/system/cleanup', null, { params: { confirm: 'CONFIRMAR' } });
      toast.success('Sistema limpo com sucesso!');
      console.log('Cleanup result:', response.data);
      setShowCleanupModal(false);
      setConfirmText('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro na limpeza');
    } finally {
      setCleaning(false);
    }
  };

  return (
    <MainLayout>
      <div className="space-y-8 page-enter" data-testid="settings-page">
        <div>
          <h1 className="text-4xl font-bold text-slate-900 mb-2" style={{ fontFamily: 'Barlow Condensed' }}>
            Configurações
          </h1>
          <p className="text-slate-600">Gerencie lojas, usuários e permissões</p>
        </div>
        
        <div className="bg-white rounded-xl shadow-sm border border-slate-100 p-6">
          <h3 className="text-xl font-bold text-slate-900 mb-4">Gestão do Sistema</h3>
          <p className="text-slate-600 mb-6">
            Gestão de lojas (matriz/filiais), usuários, perfis e permissões (RBAC).
          </p>
          <p className="text-sm text-slate-500">(Módulo em desenvolvimento)</p>
        </div>

        {/* Área de Perigo */}
        <div className="bg-red-50 border border-red-200 rounded-xl p-6">
          <h3 className="text-xl font-bold text-red-700 mb-2 flex items-center gap-2">
            <AlertTriangle size={24} />
            Zona de Perigo
          </h3>
          <p className="text-red-600 mb-4">
            Ações irreversíveis que afetam todo o sistema.
          </p>
          
          <div className="bg-white border border-red-200 rounded-lg p-4">
            <div className="flex items-center justify-between">
              <div>
                <h4 className="font-semibold text-slate-900">Limpar Todos os Pedidos</h4>
                <p className="text-sm text-slate-600">
                  Remove todos os pedidos, reseta financeiro e estoque para zero.
                </p>
              </div>
              <Button
                variant="destructive"
                onClick={() => setShowCleanupModal(true)}
                className="bg-red-600 hover:bg-red-700"
                data-testid="cleanup-btn"
              >
                <Trash2 size={16} className="mr-2" />
                Limpar Sistema
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Modal de Confirmação */}
      <Dialog open={showCleanupModal} onOpenChange={setShowCleanupModal}>
        <DialogContent className="bg-white">
          <DialogHeader>
            <DialogTitle className="text-red-600 flex items-center gap-2">
              <AlertTriangle size={24} />
              Confirmar Limpeza do Sistema
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <p className="text-red-700 font-medium mb-2">Esta ação irá:</p>
              <ul className="text-sm text-red-600 list-disc pl-5 space-y-1">
                <li>Deletar TODOS os pedidos do sistema</li>
                <li>Zerar todo o financeiro (contas a pagar/receber)</li>
                <li>Zerar todo o estoque</li>
                <li>Liberar todas as mesas</li>
                <li>Fechar sessões de caixa abertas</li>
              </ul>
              <p className="text-red-700 font-bold mt-3">Esta ação é IRREVERSÍVEL!</p>
            </div>
            
            <div>
              <p className="text-sm text-slate-600 mb-2">
                Digite <strong>CONFIRMAR</strong> para prosseguir:
              </p>
              <Input
                value={confirmText}
                onChange={(e) => setConfirmText(e.target.value.toUpperCase())}
                placeholder="CONFIRMAR"
                className="border-red-300 focus:border-red-500"
                data-testid="confirm-cleanup-input"
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowCleanupModal(false); setConfirmText(''); }}>
              Cancelar
            </Button>
            <Button
              variant="destructive"
              onClick={handleCleanup}
              disabled={cleaning || confirmText !== 'CONFIRMAR'}
              className="bg-red-600 hover:bg-red-700"
              data-testid="confirm-cleanup-btn"
            >
              {cleaning ? (
                <>
                  <Loader2 size={16} className="mr-2 animate-spin" />
                  Limpando...
                </>
              ) : (
                <>
                  <Trash2 size={16} className="mr-2" />
                  Executar Limpeza
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </MainLayout>
  );
}
